<template>
  <div class="core-layout-header !bg-neutral-800 w-full shadow-lg px-[65px] fixed z-10">
    <div>
      <Menubar
        class="bg-transparent border-none p-0 py-3"
        breakpoint="1023px"
        :pt="{
          menu: 'lg:flex lg:w-full justify-end pr-8',
          start: 'w-full  lg:w-fit',
          submenu: 'w-full px-2 py-4 rounded-xl left-[-150px]'
        }"
        :model="currentMenu"
      >
        <template #start>
          <router-link :to="{ name: 'Home' }">
            <img
              src="@/assets/Header-Logo.svg"
              class="mr-6"
            />
          </router-link>
        </template>

        <template #item="{ item, props, hasSubmenu, root }">
          <TheHeaderItem
            :root="root"
            :item="item"
            :props="props"
            :has-submenu="hasSubmenu"
          />
        </template>
      </Menubar>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSalespointStore } from '@/store';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import type { MenuItem } from 'primevue/menuitem';
import { useRouter } from 'vue-router';
import Menubar from 'primevue/menubar';
import { mortgageRouteNames } from '@/pages/mortgage';
import { useSelectSalespointModel } from '@/features/salespoint';
import TheHeaderItem from './TheHeaderItem.vue';
import { useAuthModel } from '@/features/auth';
import { routesNames } from '@/app/router';
import { useChatmessageModel } from '@/entities/chatmessage';
import { storeToRefs } from 'pinia';

type CoreLayoutMenuItem = MenuItem & {
  iconClass?: string;
  labelClass?: string;
  tooltip?: string;
};

const router = useRouter();

const authModel = useAuthModel();

const salespointStore = useSalespointStore();

const { numberOfUnreadMessages } = storeToRefs(useChatmessageModel());

const { openModal: openSelectSalespointModal } = useSelectSalespointModel();

const width = ref(window.innerWidth);

const onResize = () => {
  width.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

// оставляем старую структуру меню для не дестопов
const currentMenu = computed<CoreLayoutMenuItem[]>(() => {
  return width.value < 1024 ? items : menuItem;
});

const initials = computed(() => {
  if (authModel.user?.last_name && authModel.user?.first_name) {
    return `${authModel.user?.last_name[0]?.toLocaleUpperCase()}. ${authModel.user?.first_name[0]?.toLocaleUpperCase()}.`;
  }

  return '';
});

const userMenuItem = reactive<CoreLayoutMenuItem>({
  label: initials.value,
  tooltip: authModel.user?.title || '',
  icon: new URL('@/assets/menu-icons/User.svg', import.meta.url).href,
  route: routesNames.user.base
});

const salespointMenuItem = reactive<CoreLayoutMenuItem>({
  label: salespointStore.salespoint?.title || '',
  tooltip: salespointStore.salespoint?.title || '',
  icon: new URL('@/assets/menu-icons/Point.svg', import.meta.url).href,
  class: 'lg:max-w-[150px]',
  command: () => openSelectSalespointModal()
});

const logoutMenuItem = reactive<CoreLayoutMenuItem>({
  label: 'Выйти',
  icon: new URL('@/assets/menu-icons/Exit.svg', import.meta.url).href,
  tooltip: 'Выйти',
  labelClass: 'lg:hidden',
  iconClass: 'w-6 h-6',
  command: () => {
    authModel.logout().then(() => {
      router.push({ name: routesNames.account.login });
    });
  }
});

const reportsMenuItem = reactive({
  label: 'Отчеты',
  icon: new URL('@/assets/menu-icons/Reports.svg', import.meta.url).href,
  visible: computed(
    () =>
      authModel.user?.raw_permissions?.includes('reports_get_own') ||
      authModel.user?.raw_permissions?.includes('reports_get_all')
  ),
  items: [
    {
      label: 'Статистика по заявкам',
      route: routesNames.reports.loan
    },
    {
      label: 'Статистика по точкам',
      route: routesNames.reports.stat,
      visible: computed(() => authModel.user?.raw_permissions?.includes('mortgage_get_bc'))
    }
  ]
});

const chatmessageMenuItemBadge = computed(() =>
  numberOfUnreadMessages.value ? numberOfUnreadMessages.value : null
);

const chatmessageMenuItem = reactive({
  label: 'Чат поддержки',
  icon: new URL('@/assets/menu-icons/Chat.svg', import.meta.url).href,
  route: routesNames.chatmessage.threads,
  badge: chatmessageMenuItemBadge
});

const domRfMenuItem = reactive({
  label: 'Дом.рф',
  icon: 'pi pi-credit-card',
  visible: computed(() => Boolean((salespointStore.salespoint as any)?.domrf_link)),
  items: [
    {
      label: 'Отправить заявку',
      route: 'Domrf'
    },
    {
      label: 'ЛК Дом.рф',
      command: () => {
        window.open(
          'https://id.domrfbank.ru/auth/realms/platform/protocol/openid-connect/auth?client_id=lk-agent&redirect_uri=https%3A%2F%2Fpartner.domrfbank.ru%2Flk-agent%2Fnews%2Fbd653622-0898-4622-a673-fb315741cc4d&state=82d71cc7-dcab-45ec-8b70-fb63fd179e4b&response_mode=fragment&response_type=code&scope=openid&nonce=30fa5e1c-f603-4826-a67a-0520d3f58815&code_challenge=WZxmBUgoKiZhIrevqgMl6xIv_OWUvYAWQAxkWDkxuFk&code_challenge_method=S256',
          '_blank'
        );
      }
    }
  ]
});

// const domaInpocketMenuItem = reactive({
//   label: 'Недвижимость',
//   icon: new URL('@/assets/menu-icons/RealEstate.svg', import.meta.url).href,
//   command: () => {
//     window.open(`https://doma.inpocket.ru/?email=${authModel.user?.email}`, '_blank');
//   }
// });

const items = reactive<CoreLayoutMenuItem[]>([
  {
    label: 'Создать заявку',
    icon: 'pi pi-plus',
    route: mortgageRouteNames.finder
  },
  {
    label: 'Список заявок',
    icon: 'pi pi-list',
    route: mortgageRouteNames.list
  },
  reportsMenuItem,
  chatmessageMenuItem,
  domRfMenuItem,
  {
    separator: true,
    class: 'w-full m-0 border-gray-300 lg:flex-grow lg:w-fit'
  },
  userMenuItem,
  salespointMenuItem,
  // domaInpocketMenuItem,
  {
    label: 'Тарифы',
    icon: 'pi pi-file-pdf',
    command: () => {
      window.open('https://doma.inpocket.ru/media/tarif_2.pdf', '_blank');
    }
  },
  {
    separator: true,
    class: 'w-full m-0 border-gray-300 lg:w-fit'
  },
  logoutMenuItem
]);

const menuItem = reactive<CoreLayoutMenuItem[]>([
  salespointMenuItem,
  userMenuItem,
  {
    icon: new URL('@/assets/menu-icons/MainMenu.svg', import.meta.url).href,
    class: 'ml-20',
    tooltip: 'Меню',
    items: [
      {
        label: 'Создать заявку',
        icon: new URL('@/assets/menu-icons/Create.svg', import.meta.url).href,
        route: mortgageRouteNames.finder
      },
      {
        label: 'Список заявок',
        icon: new URL('@/assets/menu-icons/List.svg', import.meta.url).href,
        route: mortgageRouteNames.list
      },
      reportsMenuItem,
      chatmessageMenuItem,
      domRfMenuItem,
      {
        label: 'Личный кабинет',
        icon: new URL('@/assets/menu-icons/Cabinet.svg', import.meta.url).href,
        route: routesNames.user.base
      },
      // domaInpocketMenuItem,
      {
        label: 'Тарифы',
        icon: new URL('@/assets/menu-icons/Tariffs.svg', import.meta.url).href,
        command: () => {
          window.open('https://doma.inpocket.ru/media/tarif_2.pdf', '_blank');
        }
      },
      {
        label: 'FAQ',
        icon: 'pi pi-question',
        route: routesNames.info.faq
      },
      {
        label: 'О нас',
        icon: 'pi pi-info',
        route: routesNames.info.about
      }
    ]
  },
  logoutMenuItem
]);

watch(
  () => salespointStore.salespoint,
  () => {
    const title = salespointStore.salespoint?.title || '';
    salespointMenuItem.label = title;
    salespointMenuItem.tooltip = title;
  }
);
</script>
